import type {VendorPixel} from '../types/analytics/vendor-pixel'

/**
 * Whether the pixel is a page-view pixel.
 *
 * When `type` is present it wins over legacy fields; otherwise we fall back to
 * the legacy `pageViewUrl` presence check.
 */
export function isPageViewPixel(p: VendorPixel): boolean {
  if (p.type === 'page-view') return true
  if (p.type) return false
  return !!p.pageViewUrl
}

/**
 * Whether the pixel is a click pixel.
 *
 * When `type` is present it wins over legacy fields; otherwise we fall back to
 * the legacy `clickEventUrl` presence check.
 */
export function isClickPixel(p: VendorPixel): boolean {
  if (p.type === 'cta-click') return true
  if (p.type) return false
  return !!p.clickEventUrl
}

/**
 * Resolves the URL to fire for a given pixel in page-view context.
 *
 * Prefers the new canonical `url` field; otherwise falls back to the legacy
 * `pageViewUrl` field.
 */
export function pageViewUrl(p: VendorPixel): string | undefined {
  return p.url ?? p.pageViewUrl
}

/**
 * Resolves the URL to fire for a given pixel in click context.
 *
 * Prefers the new canonical `url` field; otherwise falls back to the legacy
 * `clickEventUrl` field.
 */
export function clickUrl(p: VendorPixel): string | undefined {
  return p.url ?? p.clickEventUrl
}

/**
 * Matches a click payload against a pixel's `targets` list.
 *
 * OR across entries: any single entry matching counts as a match.
 * AND within an entry: every (key, value) pair in the entry must equal the
 * corresponding value in `payload`.
 *
 * Absent or empty `targets` matches any (non-null) payload — unscoped pixels
 * fire on every analytics-event click in the tracker tree.
 */
export function matchesTarget(
  targets: Array<Record<string, string>> | undefined,
  payload: Record<string, unknown> | null,
): boolean {
  if (!targets || targets.length === 0) return true
  if (!payload) return false
  return targets.some(target => Object.entries(target).every(([k, v]) => payload[k] === v))
}

/**
 * Composite dedup key for click pixels.
 *
 * Keyed by `pixel.key` plus a stable serialization of the matched click
 * payload. This ensures:
 *  - the same pixel on the same CTA fires once;
 *  - the same pixel on a different CTA fires again (different payload);
 *  - a null payload (legacy / malformed JSON) still gets a stable key.
 */
export function pixelDedupKey(pixelKey: string, payload: Record<string, unknown> | null): string {
  if (!payload) return `${pixelKey}:`
  const sorted = Object.keys(payload)
    .sort()
    .map(k => `${encodeURIComponent(k)}=${encodeURIComponent(String(payload[k]))}`)
    .join('&')
  return `${pixelKey}:${sorted}`
}
